import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import WatchlistTab from "@/pages/SohelWatchlist";
import {
  RefreshCw,
  Sparkles,
  Flag,
  TrendingUp,
  TrendingDown,
  Activity,
  ExternalLink,
  Brain,
  Layers,
  Target,
  Zap,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  ArrowRight,
  Search,
  Bell,
  BellOff,
  Flame,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const DIM_META = {
  fundamental:      { label: "Fundamental",      icon: Layers,   accent: "text-emerald-300", ring: "ring-emerald-400/30" },
  technical:        { label: "Technical",        icon: Activity, accent: "text-sky-300",     ring: "ring-sky-400/30" },
  unusual_activity: { label: "Unusual Activity", icon: Zap,      accent: "text-amber-300",   ring: "ring-amber-400/30" },
  catalyst:         { label: "Catalyst",         icon: Sparkles, accent: "text-fuchsia-300", ring: "ring-fuchsia-400/30" },
};

// =============================================================================
// 🔔 PUSH NOTIFICATION HELPERS
// =============================================================================
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function PushNotificationsButton() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      // 1. Permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications blocked by your browser/OS");
        return;
      }
      // 2. VAPID public key
      const r = await api.get("/push/vapid-public-key");
      const publicKey = r.data?.public_key;
      if (!publicKey) {
        toast.error("Server push key missing");
        return;
      }
      // 3. Subscribe via SW
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      // 4. Send to backend
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setSubscribed(true);
      toast.success("Push notifications enabled — you'll be alerted on every Fully Vetted pick.");
    } catch (e) {
      toast.error("Failed to enable: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled.");
    } catch (e) {
      toast.error("Failed to disable: " + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      toast.success(`Test sent → ${r.data?.sent || 0} device(s)`);
    } catch {
      toast.error("Test send failed");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Badge className="bg-zinc-500/20 text-zinc-400 border border-zinc-700">
        <BellOff className="w-3 h-3 mr-1" /> Push unsupported on this browser
      </Badge>
    );
  }

  if (permission === "denied") {
    return (
      <Badge className="bg-rose-500/20 text-rose-300 border border-rose-400/40" data-testid="push-blocked">
        <BellOff className="w-3 h-3 mr-1" /> Blocked — enable in browser settings
      </Badge>
    );
  }

  if (subscribed) {
    return (
      <div className="flex items-center gap-2">
        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/40" data-testid="push-active">
          <Bell className="w-3 h-3 mr-1" /> Alerts ON
        </Badge>
        <Button
          data-testid="push-test-btn"
          size="sm"
          variant="ghost"
          onClick={sendTest}
          disabled={busy}
          className="h-8 text-xs"
        >
          Send test
        </Button>
        <Button
          data-testid="push-disable-btn"
          size="sm"
          variant="ghost"
          onClick={disable}
          disabled={busy}
          className="h-8 text-xs text-rose-300 hover:text-rose-200"
        >
          Disable
        </Button>
      </div>
    );
  }

  return (
    <Button
      data-testid="push-enable-btn"
      size="sm"
      onClick={enable}
      disabled={busy}
      className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white"
    >
      <Bell className="w-4 h-4 mr-2" />
      {busy ? "Enabling…" : "Enable Fully-Vetted alerts"}
    </Button>
  );
}

function StatusBadge({ status }) {
  const map = {
    FULLY_VETTED: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40",
    REVIEW:       "bg-amber-500/20 text-amber-300 border-amber-400/40",
    INCOMPLETE:   "bg-zinc-500/20 text-zinc-300 border-zinc-400/40",
  };
  return (
    <Badge data-testid={`synthesis-status-${status}`}
           className={`${map[status] || map.INCOMPLETE} border text-[10px] uppercase tracking-wider`}>
      {status === "FULLY_VETTED" ? "✓ Fully Vetted" : status?.replace("_", " ")}
    </Badge>
  );
}

function DimensionPill({ dim, name }) {
  const meta = DIM_META[name];
  const Icon = meta.icon;
  const filled = dim?.filled;
  const score = dim?.score || 0;
  return (
    <div
      data-testid={`synthesis-dim-${name}`}
      className={`flex flex-col gap-1 rounded-lg p-2 border ${
        filled
          ? `bg-white/5 border-white/10 ring-1 ${meta.ring}`
          : "bg-black/40 border-zinc-800 opacity-60"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
        <Icon className={`w-3 h-3 ${filled ? meta.accent : "text-zinc-500"}`} />
        <span className={filled ? meta.accent : "text-zinc-500"}>{meta.label}</span>
        {filled
          ? <CheckCircle2 className="w-3 h-3 text-emerald-400 ml-auto" />
          : <Clock className="w-3 h-3 text-zinc-600 ml-auto" />}
      </div>
      <div className={`text-lg font-data font-bold ${filled ? "text-white" : "text-zinc-600"}`}>
        {filled ? score : "—"}
      </div>
    </div>
  );
}

function SynthesisCard({ pick }) {
  const dims = pick.dimensions || {};
  const completeness = pick.completeness || 0;
  return (
    <Card data-testid={`synthesis-card-${pick.symbol}`} className="card-dark border-border overflow-hidden">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-bold font-data text-white">{pick.symbol}</h3>
              <StatusBadge status={pick.status} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Last refreshed {new Date(pick.last_updated).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Composite</div>
            <div className="text-3xl font-data font-bold text-white">{pick.composite_score}</div>
            <div className="text-[10px] text-muted-foreground">{completeness}% filled</div>
          </div>
        </div>

        {/* Completeness bar */}
        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              pick.status === "FULLY_VETTED" ? "bg-emerald-500" :
              pick.status === "REVIEW" ? "bg-amber-500" : "bg-sky-500"
            }`}
            style={{ width: `${completeness}%` }}
          />
        </div>

        {/* Dimension grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {["fundamental", "technical", "unusual_activity", "catalyst"].map((d) => (
            <DimensionPill key={d} name={d} dim={dims[d]} />
          ))}
        </div>

        {/* Verdict snippets */}
        {dims.fundamental?.filled && dims.fundamental.data?.verdict && (
          <div className="text-[11px] text-muted-foreground">
            <span className="text-emerald-300">Fund:</span> {dims.fundamental.data.verdict.toUpperCase()} — {dims.fundamental.data.key_strength?.slice(0, 100)}
          </div>
        )}
        {dims.technical?.filled && (
          <div className="text-[11px] text-muted-foreground">
            <span className="text-sky-300">Tech:</span> {dims.technical.data?.bias} ({dims.technical.data?.confidence}) · R/R {dims.technical.data?.rr?.toFixed?.(2)} · MTF {dims.technical.data?.mtf}
          </div>
        )}
        {dims.catalyst?.filled && dims.catalyst.data?.description && (
          <div className="text-[11px] text-muted-foreground">
            <span className="text-fuchsia-300">Catalyst:</span> {dims.catalyst.data.description}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SynthesisTab() {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [customSymbols, setCustomSymbols] = useState("");

  const fetchPicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "ALL" ? `?status=${statusFilter}&limit=50` : "?limit=50";
      const r = await api.get(`/sohel-agent/synthesis/picks${params}`);
      setPicks(r.data?.picks || []);
    } catch (e) {
      toast.error("Failed to load synthesis picks");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  const triggerRun = async () => {
    setRunning(true);
    try {
      const symbols = customSymbols
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const body = symbols.length ? { symbols } : {};
      await api.post("/sohel-agent/synthesis/run", body);

      if (symbols.length) {
        toast.info(`Synthesis running on ${symbols.join(", ")} — checking every 5s for ~1min…`, {
          duration: 4000,
        });
        // Poll up to 12× / 60s, watching for the symbol(s) to appear/update.
        // Stop early once we've detected updates for all submitted symbols.
        const startedAt = Date.now();
        const beforeMap = new Map(picks.map((p) => [p.symbol, p.last_updated]));
        let detected = new Set();
        for (let i = 0; i < 12; i++) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const params = statusFilter && statusFilter !== "ALL"
              ? `?status=${statusFilter}&limit=50` : "?limit=50";
            const r = await api.get(`/sohel-agent/synthesis/picks${params}`);
            const nextPicks = r.data?.picks || [];
            setPicks(nextPicks);
            // Check whether each submitted symbol has been freshly updated
            for (const sym of symbols) {
              const found = nextPicks.find((p) => p.symbol === sym);
              if (found && found.last_updated !== beforeMap.get(sym)) {
                detected.add(sym);
              }
            }
            if (detected.size === symbols.length) {
              toast.success(`${symbols.length === 1 ? symbols[0] : symbols.length + " symbols"} synthesized.`);
              break;
            }
          } catch { /* keep polling */ }
        }
        if (detected.size < symbols.length) {
          const missing = symbols.filter((s) => !detected.has(s));
          toast.warning(`Still working: ${missing.join(", ")}. They'll appear on next refresh.`);
        }
      } else {
        toast.success("Default-sweep synthesis triggered (active picks + recent candidates).");
        setTimeout(fetchPicks, 12000);
      }
      setCustomSymbols("");
    } catch (e) {
      toast.error("Failed to trigger synthesis");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="card-dark border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Brain className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-white">4-Pillar Synthesis Picks</h2>
              <p className="text-xs text-muted-foreground">
                Agents continuously scoop info across <span className="text-emerald-300">Fundamental</span>,{" "}
                <span className="text-sky-300">Technical</span>,{" "}
                <span className="text-amber-300">Unusual Activity</span>, and{" "}
                <span className="text-fuchsia-300">Catalyst</span> dimensions. A pick is promoted to
                <span className="text-emerald-300"> "Fully Vetted"</span> only when all 4 are filled & composite ≥ 65.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              data-testid="synthesis-custom-symbols"
              value={customSymbols}
              onChange={(e) => setCustomSymbols(e.target.value)}
              placeholder="Symbols (e.g. AAPL, NVDA, PLTR)"
              className="bg-black/40 border-zinc-800 text-white max-w-xs h-9"
            />
            <Button
              data-testid="synthesis-run-btn"
              onClick={triggerRun}
              disabled={running}
              className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {running ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              {running ? "Synthesizing… up to 1 min" : "Run Synthesis"}
            </Button>
            <Button
              data-testid="synthesis-refresh-btn"
              variant="ghost"
              onClick={fetchPicks}
              disabled={loading}
              className="h-9"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
            <TabsList className="bg-black/40 border border-zinc-800">
              <TabsTrigger data-testid="synthesis-tab-all"          value="ALL">All</TabsTrigger>
              <TabsTrigger data-testid="synthesis-tab-vetted"       value="FULLY_VETTED">✓ Fully Vetted</TabsTrigger>
              <TabsTrigger data-testid="synthesis-tab-review"       value="REVIEW">Review</TabsTrigger>
              <TabsTrigger data-testid="synthesis-tab-incomplete"   value="INCOMPLETE">Incomplete</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Loading synthesis data…
          </CardContent>
        </Card>
      ) : picks.length === 0 ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center space-y-2">
            <Brain className="w-8 h-8 mx-auto text-zinc-600" />
            <p className="text-sm text-muted-foreground">
              No synthesis picks yet. Enter symbols above and hit Run Synthesis, or wait for the hourly sweep.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {picks.map((p) => <SynthesisCard key={p.symbol} pick={p} />)}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TRUMP TRACKER TAB
// =============================================================================
function SentimentBadge({ sentiment, direction }) {
  const s = (sentiment || direction || "").toUpperCase();
  if (s === "BULLISH" || s === "UP")
    return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/40 border"><TrendingUp className="w-3 h-3 mr-1" />{s}</Badge>;
  if (s === "BEARISH" || s === "DOWN")
    return <Badge className="bg-rose-500/20 text-rose-300 border-rose-400/40 border"><TrendingDown className="w-3 h-3 mr-1" />{s}</Badge>;
  return <Badge className="bg-zinc-500/20 text-zinc-300 border-zinc-400/40 border">{s || "NEUTRAL"}</Badge>;
}

function TruthCard({ s }) {
  return (
    <Card data-testid={`trump-truth-${s.signal_id}`} className="card-dark border-border">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <Flag className="w-3.5 h-3.5 text-amber-300" />
            <span className="font-semibold text-amber-200">Donald Trump</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground text-[11px]">{s.created_at || "recent"}</span>
          </div>
          <SentimentBadge sentiment={s.sentiment} />
        </div>
        <p className="text-sm text-zinc-100 whitespace-pre-line">{s.text?.slice(0, 600)}{s.text?.length > 600 ? "…" : ""}</p>
        {(s.tickers || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {s.tickers.map((t) => (
              <Badge key={t} className="bg-amber-500/20 text-amber-200 border-amber-400/40 border font-data">{t}</Badge>
            ))}
          </div>
        )}
        {s.rationale && (
          <p className="text-[11px] text-muted-foreground italic border-t border-zinc-800 pt-2">
            <span className="text-emerald-300">AI:</span> {s.rationale}
          </p>
        )}
        <div className="flex items-center justify-between pt-1">
          <span className="text-[10px] text-muted-foreground">Confidence: {s.confidence ?? 0}/100</span>
          <a href={s.url} target="_blank" rel="noreferrer"
             className="text-[11px] text-sky-300 hover:underline inline-flex items-center gap-1">
            View post <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function CapitolCard({ s }) {
  return (
    <Card data-testid={`trump-capitol-${s.signal_id}`} className="card-dark border-border">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <Target className="w-3.5 h-3.5 text-rose-300" />
            <span className="font-semibold text-rose-200">{s.actor}</span>
            {s.chamber && <span className="text-[10px] text-muted-foreground">· {s.chamber}</span>}
            {s.party && <span className="text-[10px] text-muted-foreground">· {s.party}</span>}
          </div>
          <SentimentBadge direction={s.direction} />
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-zinc-800 text-white border-zinc-700 border font-data">{s.ticker}</Badge>
          {s.asset_name && <span className="text-xs text-muted-foreground">{s.asset_name}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          {s.tx_type && <span><span className="text-zinc-500">Type:</span> <span className="text-white uppercase">{s.tx_type}</span></span>}
          {s.tx_date && <span><span className="text-zinc-500">Date:</span> {s.tx_date}</span>}
          {s.value_range && <span><span className="text-zinc-500">Size:</span> {s.value_range}</span>}
        </div>
        {s.rationale && (
          <p className="text-[11px] text-muted-foreground italic border-t border-zinc-800 pt-2">
            <span className="text-emerald-300">AI:</span> {s.rationale}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function FundCard({ s }) {
  return (
    <Card data-testid={`trump-1789-${s.signal_id}`} className="card-dark border-border">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
            <span className="font-semibold text-fuchsia-200">{s.actor}</span>
          </div>
          <SentimentBadge direction={s.direction} />
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-zinc-800 text-white border-zinc-700 border font-data">{s.ticker}</Badge>
          {s.asset_name && <span className="text-xs text-muted-foreground">{s.asset_name}</span>}
        </div>
        {s.rationale && (
          <p className="text-[11px] text-muted-foreground italic">{s.rationale}</p>
        )}
      </CardContent>
    </Card>
  );
}

function TrumpTrackerTab() {
  const [signals, setSignals] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [kind, setKind] = useState("ALL");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const kindParam = kind && kind !== "ALL" ? `&kind=${kind}` : "";
      const [sigRes, stRes] = await Promise.all([
        api.get(`/sohel-agent/trump-tracker/signals?limit=80${kindParam}`),
        api.get(`/sohel-agent/trump-tracker/status`),
      ]);
      setSignals(sigRes.data?.signals || []);
      setStatus(stRes.data || null);
    } catch (e) {
      toast.error("Failed to load Trump Tracker");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const triggerRun = async () => {
    setRunning(true);
    try {
      await api.post("/sohel-agent/trump-tracker/run");
      toast.success("Trump Tracker sweep started");
      setTimeout(fetchAll, 15000);
    } catch (e) {
      toast.error("Failed to trigger sweep");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="card-dark border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Flag className="w-5 h-5 text-amber-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-white">Trump Tracker</h2>
              <p className="text-xs text-muted-foreground">
                Monitors <span className="text-amber-300">Trump's Truth Social</span> posts,{" "}
                <span className="text-rose-300">Capitol Trades</span> (Trump family + cabinet & allies), and{" "}
                <span className="text-fuchsia-300">1789 Capital</span> holdings. Updated hourly.
              </p>
            </div>
            <Button
              data-testid="trump-tracker-run-btn"
              onClick={triggerRun}
              disabled={running}
              className="h-9 bg-amber-600 hover:bg-amber-500 text-white"
            >
              {running ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Run Now
            </Button>
          </div>

          {status?.last_run && (
            <div className="text-[11px] text-muted-foreground border-t border-zinc-800 pt-2 flex flex-wrap gap-3">
              <span>Last sweep <span className="text-white">{new Date(status.last_run.completed_at).toLocaleString()}</span></span>
              <span>·</span>
              <span>Truth posts: <span className="text-amber-300">{status.by_kind?.truth_social_post ?? 0}</span></span>
              <span>·</span>
              <span>Capitol trades: <span className="text-rose-300">{status.by_kind?.capitol_trade ?? 0}</span></span>
              <span>·</span>
              <span>1789 holdings: <span className="text-fuchsia-300">{status.by_kind?.["1789_capital_holding"] ?? 0}</span></span>
            </div>
          )}

          <Tabs value={kind} onValueChange={(v) => setKind(v)}>
            <TabsList className="bg-black/40 border border-zinc-800">
              <TabsTrigger data-testid="trump-tab-all"     value="ALL">All</TabsTrigger>
              <TabsTrigger data-testid="trump-tab-truth"   value="truth_social_post">Truth Social</TabsTrigger>
              <TabsTrigger data-testid="trump-tab-capitol" value="capitol_trade">Capitol Trades</TabsTrigger>
              <TabsTrigger data-testid="trump-tab-fund"    value="1789_capital_holding">1789 Capital</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">Loading signals…</CardContent>
        </Card>
      ) : signals.length === 0 ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center space-y-2">
            <Flag className="w-8 h-8 mx-auto text-zinc-600" />
            <p className="text-sm text-muted-foreground">No signals yet — hit Run Now or wait for the hourly sweep.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {signals.map((s) => {
            if (s.kind === "truth_social_post")    return <TruthCard   key={s.signal_id} s={s} />;
            if (s.kind === "capitol_trade")        return <CapitolCard key={s.signal_id} s={s} />;
            if (s.kind === "1789_capital_holding") return <FundCard    key={s.signal_id} s={s} />;
            return null;
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DISCOVERY FUNNEL TAB
// =============================================================================
const STAGE_META = {
  PROMOTED:     { label: "Promoted",     color: "text-emerald-300", bg: "bg-emerald-500/20", border: "border-emerald-400/40", rank: 0 },
  VETTED:       { label: "Fully Vetted", color: "text-sky-300",     bg: "bg-sky-500/20",     border: "border-sky-400/40",     rank: 1 },
  SYNTHESIZING: { label: "Synthesizing", color: "text-amber-300",   bg: "bg-amber-500/20",   border: "border-amber-400/40",   rank: 2 },
  DISCOVERED:   { label: "Discovered",   color: "text-zinc-300",    bg: "bg-zinc-500/20",    border: "border-zinc-400/40",    rank: 3 },
};

const SOURCE_BADGE = {
  trump_tracker:       { label: "🇺🇸 Trump",      color: "bg-amber-500/15 text-amber-300" },
  synthesis_agent:     { label: "🧩 Synthesis",   color: "bg-emerald-500/15 text-emerald-300" },
  earnings_eve_agent:  { label: "📅 Earnings",    color: "bg-fuchsia-500/15 text-fuchsia-300" },
  edgar_form4:         { label: "🏛️ Form 4",     color: "bg-sky-500/15 text-sky-300" },
  openfda:             { label: "💊 FDA",         color: "bg-rose-500/15 text-rose-300" },
  reddit_stocktwits:   { label: "💬 Social",      color: "bg-violet-500/15 text-violet-300" },
  news_catalyst:       { label: "📰 News",        color: "bg-cyan-500/15 text-cyan-300" },
};

function StageStat({ label, value, color, icon: Icon }) {
  return (
    <div className="flex-1 min-w-[120px] bg-black/40 border border-zinc-800 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className={`w-3 h-3 ${color}`} />}
        {label}
      </div>
      <div className={`text-2xl font-data font-bold mt-1 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

function FunnelRow({ row }) {
  const meta = STAGE_META[row.stage] || STAGE_META.DISCOVERED;
  const synth = row.synthesis;
  const promise = row.promise;
  const filledDims = synth ? Object.values(synth.dimensions || {}).filter((d) => d.filled).length : 0;

  return (
    <Card data-testid={`funnel-row-${row.symbol}`} className="card-dark border-border overflow-hidden">
      <CardContent className="p-3">
        <div className="grid grid-cols-12 gap-3 items-center">
          {/* Symbol + stage */}
          <div className="col-span-3">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold font-data text-white">{row.symbol}</span>
              <Badge className={`${meta.bg} ${meta.color} ${meta.border} border text-[10px] uppercase`}>
                {meta.label}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{row.latest_catalyst || "—"}</p>
          </div>

          {/* Sources */}
          <div className="col-span-3 flex flex-wrap gap-1">
            {row.sources.slice(0, 4).map((src) => {
              const sm = SOURCE_BADGE[src] || { label: src, color: "bg-zinc-500/15 text-zinc-300" };
              return (
                <Badge key={src} className={`${sm.color} border-0 text-[10px]`}>{sm.label}</Badge>
              );
            })}
            {row.sources.length > 4 && (
              <span className="text-[10px] text-muted-foreground">+{row.sources.length - 4}</span>
            )}
          </div>

          {/* Synthesis pillars */}
          <div className="col-span-3">
            {synth ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  {["fundamental", "technical", "unusual_activity", "catalyst"].map((d) => {
                    const dim = synth.dimensions[d];
                    return (
                      <div
                        key={d}
                        title={`${d}: ${dim.score}`}
                        className={`flex-1 h-2 rounded-full ${
                          dim.filled
                            ? d === "fundamental"      ? "bg-emerald-400" :
                              d === "technical"        ? "bg-sky-400" :
                              d === "unusual_activity" ? "bg-amber-400" :
                                                         "bg-fuchsia-400"
                            : "bg-zinc-800"
                        }`}
                      />
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center justify-between">
                  <span>{filledDims}/4 pillars</span>
                  <span className="font-data text-white">composite {synth.composite_score}</span>
                </div>
              </div>
            ) : (
              <span className="text-[11px] text-muted-foreground">not yet synthesized</span>
            )}
          </div>

          {/* Promise / outcome */}
          <div className="col-span-3 text-right">
            {promise ? (
              <div>
                <Badge className={`${promise.direction === "DOWN" ? "bg-rose-500/20 text-rose-300 border-rose-400/40" : "bg-emerald-500/20 text-emerald-300 border-emerald-400/40"} border text-[10px]`}>
                  {promise.direction} · conf {promise.confidence}
                </Badge>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Entry ${promise.entry_price?.toFixed(2)} · TP ${promise.target_price?.toFixed(2)}
                </div>
                {typeof promise.current_pnl_pct === "number" && (
                  <div className={`text-[11px] font-data mt-0.5 ${promise.current_pnl_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {promise.current_pnl_pct >= 0 ? "+" : ""}{promise.current_pnl_pct.toFixed(2)}%
                  </div>
                )}
              </div>
            ) : (
              <span className="text-[11px] text-muted-foreground">no Promise pick yet</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [stage, setStage] = useState("ALL");
  const [query, setQuery] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sohel-agent/funnel?days=${days}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load funnel");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = (data?.rows || []).filter((r) => {
    if (stage !== "ALL" && r.stage !== stage) return false;
    if (query && !r.symbol.toUpperCase().includes(query.toUpperCase())) return false;
    return true;
  });

  const counts = data?.counts || {};

  return (
    <div className="space-y-4">
      <Card className="card-dark border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-sky-500/10">
              <Filter className="w-5 h-5 text-sky-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-white">Discovery Funnel</h2>
              <p className="text-xs text-muted-foreground">
                Every symbol our agents have surfaced in the last {days} days — flowing from{" "}
                <span className="text-zinc-300">raw discovery</span> →{" "}
                <span className="text-amber-300">synthesis</span> →{" "}
                <span className="text-sky-300">fully vetted</span> →{" "}
                <span className="text-emerald-300">promoted to Promise pick</span>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {[1, 7, 30].map((d) => (
                <Button
                  key={d}
                  data-testid={`funnel-days-${d}`}
                  size="sm"
                  variant={days === d ? "default" : "outline"}
                  onClick={() => setDays(d)}
                  className="h-8 text-xs"
                >{d}d</Button>
              ))}
              <Button
                data-testid="funnel-refresh-btn"
                size="sm"
                variant="ghost"
                onClick={fetchData}
                disabled={loading}
                className="h-8"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {/* Funnel KPIs */}
          <div className="flex flex-wrap gap-2">
            <StageStat label="Discovered"   value={counts.discovered ?? 0}   color="text-zinc-300"    icon={Search} />
            <ArrowRight className="w-4 h-4 text-zinc-600 self-center" />
            <StageStat label="Synthesizing" value={counts.synthesizing ?? 0} color="text-amber-300"   icon={Brain} />
            <ArrowRight className="w-4 h-4 text-zinc-600 self-center" />
            <StageStat label="Fully Vetted" value={counts.vetted ?? 0}       color="text-sky-300"     icon={CheckCircle2} />
            <ArrowRight className="w-4 h-4 text-zinc-600 self-center" />
            <StageStat label="Promoted"     value={counts.promoted ?? 0}     color="text-emerald-300" icon={Target} />
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Input
              data-testid="funnel-search"
              placeholder="Filter by symbol…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-black/40 border-zinc-800 text-white h-8 max-w-[180px] text-xs"
            />
            <Tabs value={stage} onValueChange={setStage}>
              <TabsList className="bg-black/40 border border-zinc-800">
                <TabsTrigger data-testid="funnel-stage-all"          value="ALL">All</TabsTrigger>
                <TabsTrigger data-testid="funnel-stage-promoted"     value="PROMOTED">Promoted</TabsTrigger>
                <TabsTrigger data-testid="funnel-stage-vetted"       value="VETTED">Vetted</TabsTrigger>
                <TabsTrigger data-testid="funnel-stage-synthesizing" value="SYNTHESIZING">Synthesizing</TabsTrigger>
                <TabsTrigger data-testid="funnel-stage-discovered"   value="DISCOVERED">Discovered</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">Loading funnel…</CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="card-dark border-border">
          <CardContent className="p-8 text-center space-y-2">
            <Filter className="w-8 h-8 mx-auto text-zinc-600" />
            <p className="text-sm text-muted-foreground">No matching symbols.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => <FunnelRow key={r.symbol} row={r} />)}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PAGE
// =============================================================================
export default function SohelAgenticAI() {
  const [tab, setTab] = useState("watchlist");

  return (
    <div className="space-y-4 pb-24" data-testid="sohel-agentic-ai-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight flex items-center gap-2">
            <Brain className="w-7 h-7 text-emerald-300" />
            Sohel Agentic AI
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Multi-agent stock intelligence — political signals, deep synthesis, and continuous discovery.
          </p>
        </div>
        <PushNotificationsButton />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="agentic-ai-tabs" className="bg-black/40 border border-zinc-800 w-full justify-start flex-wrap h-auto">
          <TabsTrigger data-testid="agentic-tab-watchlist" value="watchlist">
            <Flame className="w-4 h-4 mr-1.5" />
            Today's Watchlist
          </TabsTrigger>
          <TabsTrigger data-testid="agentic-tab-funnel" value="funnel">
            <Filter className="w-4 h-4 mr-1.5" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger data-testid="agentic-tab-synthesis" value="synthesis">
            <Brain className="w-4 h-4 mr-1.5" />
            Synthesis Picks
          </TabsTrigger>
          <TabsTrigger data-testid="agentic-tab-trump" value="trump">
            <Flag className="w-4 h-4 mr-1.5" />
            Trump Tracker
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "watchlist" && <WatchlistTab />}
      {tab === "funnel" && <FunnelTab />}
      {tab === "synthesis" && <SynthesisTab />}
      {tab === "trump" && <TrumpTrackerTab />}
    </div>
  );
}
